import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { DynamicSection } from './DynamicSection';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import masterSync from '@/lib/masterSync';
import { useNetwork } from '@/lib/networkService';
import { OPERATIONS_SYNC_PENDING_CHANGED_EVENT } from '@/lib/operations/offlineSyncEvents';
import { type FormThemeConfig, resolveFormTheme, isThemeDark, resolvePatternCSS, buildFormThemeCss } from './formThemes';
import { resolveFormLayout, buildFormSteps, getStepSection, isAutoAdvanceField, stepButtonContainerClass, stepButtonWidthClass, listSubmitContainerClass, listSubmitButtonClass, scopeCustomCSS, type FormLayoutConfig, type FormStep } from './formLayout';
import { StepTransitionContainer } from './StepTransition';
import FormCoverPage, { type CoverPageConfig } from './FormCoverPage';
import FormSuccessPage, { type SuccessPageConfig } from './FormSuccessPage';
import { Save, X, Loader2, ChevronLeft, ChevronRight, Calculator, WifiOff } from 'lucide-react';
import { evaluateComputedFields, type ComputedFieldDef, type ComputedFieldResult } from './engine/computedFields';
import {
  evaluateRules,
  resolveBranchingHiddenSections,
  collectFieldValidationErrors,
  type RuleDef,
  type BranchingSection,
} from './engine';
import { useSwipeNavigation } from '@/hooks/useSwipeNavigation';
import { SectionLoader } from '@/components/shared/BrandedLoadingSpinner';
import type { FormConfiguration, FormSection, FormField, FormFieldOption } from './formConfigurationUtils';
import type { FieldValue, FormDataValues } from './formFieldTypes';
import { FormRuntimeKeyProvider } from './formRuntimeContext';
import { notify } from '@/lib/notifications';

interface DynamicFormBuilderProps {
  formKey: string;
  formData: FormDataValues;
  onDataChange: (data: FormDataValues) => void;
  onSubmit?: (data: FormDataValues) => void;
  onCancel?: () => void;
  mode?: 'create' | 'edit' | 'view';
  entityType?: string;
  entityId?: string | number;
  initialData?: FormDataValues;
  className?: string;
  showActions?: boolean;
  showHeader?: boolean;
  submitButtonText?: string;
  cancelButtonText?: string;
  enableImageUpload?: boolean; // Enable image upload for URL fields
  customSchema?: FormConfiguration; // Override API fetch with a pre-loaded schema
  /** Optional form theme (from theme_config on form_configurations) */
  formTheme?: Partial<FormThemeConfig> | null;
  /** Force single-column mobile layout (used by preview modal to simulate small viewports) */
  forceMobileLayout?: boolean;
}

// Cheap content hash for customSchema dedup — distinguishes real edits from
// reference churn. Falls back to an empty string for non-serializable input
// (dedup then behaves as before, keyed on form_key + version only).
const hashFormSchemaContent = (schema: FormConfiguration): string => {
  try {
    const json = JSON.stringify(schema);
    let hash = 5381;
    for (let i = 0; i < json.length; i++) {
      hash = ((hash << 5) + hash + json.charCodeAt(i)) | 0;
    }
    return hash.toString(36);
  } catch {
    return '';
  }
};

export const DynamicFormBuilder: React.FC<DynamicFormBuilderProps> = ({
  formKey,
  formData,
  onDataChange,
  mode = 'edit',
  entityType = 'employee',
  entityId,
  initialData = {},
  onSubmit,
  onCancel,
  className = '',
  showActions = true,
  showHeader = true,
  submitButtonText = 'Save',
  cancelButtonText = 'Cancel',
  enableImageUpload = false,
  customSchema,
  formTheme: formThemeProp,
  forceMobileLayout = false,
}) => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';

  const [formConfig, setFormConfig] = useState<FormConfiguration | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, FormFieldOption[]>>({});
  const [draftStatus, setDraftStatus] = useState<'none' | 'available' | 'restored' | 'saving'>('none');
  const [draftTimestamp, setDraftTimestamp] = useState<string | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Phase 9: Offline queue ───────────────────────────────────
  const { isConnected: isOnline } = useNetwork();

  // ── Phase 5: Cover page & success page state ─────────────────
  const [showCoverPage, setShowCoverPage] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  // ── Phase 6: Wizard / stepper state ──────────────────────────
  const [wizardStep, setWizardStep] = useState(0);

  // ── Layout / view-mode config from form_schema ───────────────
  const layout = useMemo(() => resolveFormLayout(formConfig?.form_schema?.layout), [formConfig]);
  const isStepView = layout.view_mode === 'step';

  // Resolve form theme — explicit prop wins, then theme_config carried by the
  // custom schema or the fetched form configuration, then the form's accent
  // color as a minimal theme. Only apply theme styles when one exists so
  // unthemed forms keep their default (dark-mode-aware) styling.
  const themeInput = useMemo<Partial<FormThemeConfig> | null>(() => {
    if (formThemeProp) return formThemeProp;
    const source = (customSchema ?? formConfig) as Record<string, unknown> | null;
    const schemaTheme = source?.theme_config as Partial<FormThemeConfig> | null | undefined;
    const color = typeof source?.color === 'string' && source.color ? source.color : undefined;
    if (schemaTheme && Object.keys(schemaTheme).length > 0) {
      if (!schemaTheme.accent_color && color) {
        return {
          ...schemaTheme,
          accent_color: color,
          header_gradient:
            schemaTheme.header_gradient ??
            `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
        };
      }
      return schemaTheme;
    }
    if (color) {
      return {
        accent_color: color,
        header_gradient: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
      };
    }
    return null;
  }, [formThemeProp, customSchema, formConfig]);
  const ft = resolveFormTheme(themeInput);
  const ftDark = isThemeDark(ft);
  const hasFormTheme = themeInput != null;

  const scopeClass = `tf-css-${formKey.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  // Generated theme CSS brings field-level elements (inputs, labels, checkable
  // accents) in line with the designer canvas — DynamicField itself only knows
  // the portal theme. User custom_css still wins via @scope ordering.
  const themeCss = hasFormTheme ? buildFormThemeCss(ft, scopeClass) : null;
  const customCSS = scopeCustomCSS(themeInput?.custom_css, scopeClass);
  const injectedCSS = [themeCss, customCSS].filter(Boolean).join('\n');
  const textSizeScale = useMemo(() => {
    if (ft.text_size === 'large') return '1.05em';
    if (ft.text_size === 'xl') return '1.12em';
    return undefined;
  }, [ft.text_size]);

  // ── Rules engine (show/hide/require/block_submit/jump/captcha) ──
  // Declared early so wizardTotalSteps and other memos can reference ruleResults.
  const ruleDefs = useMemo<RuleDef[]>(() => {
    const rules = formConfig?.form_schema?.rules;
    return Array.isArray(rules) ? rules as RuleDef[] : [];
  }, [formConfig]);
  const ruleResults = useMemo(() => {
    if (!ruleDefs.length) return null;
    return evaluateRules(ruleDefs, formData);
  }, [ruleDefs, formData]);

  // Section branching + rules-engine section visibility
  const branchingHiddenSections = useMemo(() => {
    const secs = formConfig?.form_schema?.sections || [];
    const sorted = [...secs].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    return resolveBranchingHiddenSections(sorted as BranchingSection[], formData);
  }, [formConfig, formData]);

  // ── Phase 9: Total steps count for wizard swipe (per-section or per-field) ─
  const wizardTotalSteps = useMemo(() => {
    const secs = formConfig?.form_schema?.sections || [];
    const isBranchingWizard = secs.some((s) => s.goto_section);
    return (isBranchingWizard || isStepView) ? buildFormSteps(secs, layout, formData, ruleResults ? {
      hiddenFields: ruleResults.hiddenFields,
      shownFields: ruleResults.shownFields,
    } : null).length : 0;
  }, [formConfig, isStepView, layout, formData, ruleResults]);

  // ── Phase 9: Swipe navigation for wizard mode ────────────────
  const { containerRef: swipeContainerRef } = useSwipeNavigation({
    onSwipeLeft: useCallback(() => {
      if (wizardTotalSteps > 0) setWizardStep(s => Math.min(s + 1, wizardTotalSteps - 1));
    }, [wizardTotalSteps]),
    onSwipeRight: useCallback(() => {
      if (wizardTotalSteps > 0) setWizardStep(s => Math.max(s - 1, 0));
    }, [wizardTotalSteps]),
    enabled: wizardTotalSteps > 0,
  });

  // ── Phase 6.4: Computed fields ───────────────────────────────
  const computedFieldResults = useMemo<ComputedFieldResult[]>(() => {
    const defs = formConfig?.form_schema?.computed_fields;
    if (!defs?.length) return [];
    const sections = formConfig?.form_schema?.sections || [];
    return evaluateComputedFields(defs, formData, sections);
  }, [formConfig, formData]);

  // ── Phase 5: Derive cover page & success page configs from form_schema ──
  // (MUST be declared here — before any early returns — to satisfy Rules of Hooks)
  const coverPageConfig: CoverPageConfig | null = useMemo(() => {
    const cp = formConfig?.form_schema?.cover_page as CoverPageConfig | undefined;
    if (cp && cp.enabled) return cp as CoverPageConfig;
    return null;
  }, [formConfig]);

  const successPageConfig: SuccessPageConfig = useMemo(() => {
    const fc = formConfig;
    return {
      message: fc?.success_message || 'Thank you for your submission!',
      redirect_url: fc?.success_redirect_url || undefined,
      redirect_delay: fc?.success_redirect_delay || 0,
      image_url: fc?.success_image_url || undefined,
      show_summary: fc?.success_show_summary ?? false,
      allow_another: fc?.success_allow_another ?? false,
    };
  }, [formConfig]);

  // Build field label map for success summary
  const fieldLabels: Record<string, string> = useMemo(() => {
    const labels: Record<string, string> = {};
    formConfig?.form_schema?.sections?.forEach((s) => {
      s.fields?.forEach((f) => { labels[f.key] = f.label; });
    });
    return labels;
  }, [formConfig]);

  // Pattern CSS for background overlay
  const patternCSS = useMemo(() => {
    if (!hasFormTheme || !ft.bg_pattern) return null;
    return resolvePatternCSS(ft);
  }, [hasFormTheme, ft]);

  // ── Branding config (per-form: logo, watermark) ──────────────
  // Reads from formConfig.branding_config — the per-form config saved
  // in the builder's Design tab. This takes highest priority over
  // any client-level branding defaults.
  const brandingConfig = (formConfig as Record<string, unknown> | null)?.branding_config as Record<string, unknown> | undefined;
  const brandLogoUrl = (brandingConfig?.brand_logo_url as string) || undefined;
  const showBranding = brandingConfig?.show_branding !== false;
  const watermarkText = (brandingConfig?.watermark_text as string) || undefined;

  // ── localStorage draft helpers ────────────────────────────────
  const draftKey = `form_draft_${formKey}_${entityId || 'new'}`;

  const refreshPendingCount = useCallback(async () => {
    const count = await masterSync.getPendingOperationalFormCount().catch(() => 0);
    setPendingCount(count);
  }, []);

  const saveDraft = useCallback(async (data: FormDataValues) => {
    if (mode === 'view') return; // Never save drafts in view mode
    try {
      // Skip saving empty data
      const hasValues = Object.values(data).some(v =>
        v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)
      );
      if (!hasValues) return;

      const savedDraft = await masterSync.saveGenericFormDraft({
        id: draftKey,
        formKey,
        formConfigId: formConfig?.id || 0,
        entityType,
        entityId,
        formData: data,
      });
      setDraftTimestamp(savedDraft.savedAt);
      setDraftStatus('saving');
      // Brief flash then revert
      setTimeout(() => setDraftStatus(prev => prev === 'saving' ? 'restored' : prev), 800);
    } catch (e) {
      console.warn('[DynamicFormBuilder] Failed to save draft:', e);
    }
  }, [draftKey, entityId, entityType, formConfig?.id, formKey, mode]);

  const loadDraft = useCallback(async (): Promise<{ data: FormDataValues; savedAt: string } | null> => {
    try {
      const draft = await masterSync.getGenericFormDraft(draftKey);
      if (draft?.formData && typeof draft.formData === 'object') {
        return { data: draft.formData as FormDataValues, savedAt: draft.savedAt };
      }
    } catch (e) {
      console.warn('[DynamicFormBuilder] Failed to load draft:', e);
    }
    return null;
  }, [draftKey]);

  const clearDraft = useCallback(async () => {
    await masterSync.clearGenericFormDraft(draftKey).catch(() => undefined);
    setDraftStatus('none');
    setDraftTimestamp(null);
  }, [draftKey]);

  // Debounced auto-save on every field change
  const scheduleDraftSave = useCallback((data: FormDataValues) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => { void saveDraft(data); }, 1000);
  }, [saveDraft]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, []);

  // Load dynamic options for fields that need them (batched into a single state update)
  const loadDynamicOptions = useCallback(async (signal?: AbortSignal) => {
    try {
      const authToken = authService.getTokens()?.access_token || null;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
      };

      const isRootForm = formKey.startsWith('root_');
      const clientsEndpoint = isRootForm ? '/api/v1/root/clients?limit=1000' : '/api/v1/clients';

      const [clientsRes, modulesRes, emailRes] = await Promise.allSettled([
        fetch(clientsEndpoint, { method: 'GET', headers, signal }),
        fetch('/api/v1/client_modules_simplified', { method: 'GET', headers, signal }),
        fetch('/api/v1/email_workflows', { method: 'GET', headers, signal }),
      ]);

      if (signal?.aborted) return;

      const batch: Record<string, FormFieldOption[]> = {};

      if (clientsRes.status === 'fulfilled' && clientsRes.value.ok) {
        const r = await clientsRes.value.json();
        const data: Array<{ id: string | number; name?: string }> = r.data || r.clients || [];
        if (Array.isArray(data) && data.length > 0) {
          const opts = data.map((c) => ({ value: String(c.id), label: `${c.name ?? ''} (ID: ${c.id})` }));
          batch.client_id = opts;
          batch.client_ids = opts;
        }
      }

      if (modulesRes.status === 'fulfilled' && modulesRes.value.ok) {
        const r = await modulesRes.value.json();
        if (r.success && r.modules) {
          batch.invited_modules = (r.modules as Array<{ module_key: string; module_name?: string }>).map((m) => ({ value: m.module_key, label: m.module_name ?? m.module_key }));
        }
      }

      if (emailRes.status === 'fulfilled' && emailRes.value.ok) {
        const r = await emailRes.value.json();
        if (r.workflows) {
          batch.email_workflow_id = (r.workflows as Array<{ id: string | number; name?: string }>).map((w) => ({ value: String(w.id), label: w.name ?? String(w.id) }));
        }
      }

      if (signal?.aborted) return;

      if (Object.keys(batch).length > 0) {
        setDynamicOptions(prev => ({ ...prev, ...batch }));
      }
    } catch (error) {
      if ((error as { name?: unknown })?.name === 'AbortError') return;
      console.error('Error loading dynamic options:', error);
    }
  }, [formKey]);

  // Transform form configuration to include dynamic options (memoized to avoid re-creating objects every render)
  const transformedFormConfig = useMemo((): FormConfiguration | null => {
    if (!formConfig) return null;

    const transformedConfig = { ...formConfig };

    if (transformedConfig.form_schema?.sections) {
      transformedConfig.form_schema.sections = transformedConfig.form_schema.sections.map(section => ({
        ...section,
        // Same normalization OperationalFormModal applies: is_required feeds the
        // header asterisk; collapsible/defaultExpanded default to true so a
        // section only collapses when the schema says so explicitly.
        required: section.is_required,
        styling: {
          ...section.styling,
          behavior: {
            ...section.styling?.behavior,
            collapsible: section.is_collapsible !== false,
            defaultExpanded: section.default_expanded !== false,
          },
        },
        fields: section.fields.map(field => {
          if (dynamicOptions[field.key]) {
            return { ...field, options: dynamicOptions[field.key] };
          }

          if (field.options && typeof field.options === 'object' && !Array.isArray(field.options)) {
            const optionsBag = field.options as Record<string, unknown>;
            // Options may be nested under options/choices/items (FormFieldEditor
            // writes both `items` and `choices`). Resolve the first array before
            // treating the record itself as a key→label map.
            const nested = [optionsBag.options, optionsBag.choices, optionsBag.items].find(Array.isArray);
            if (nested) {
              const optionList = (nested as unknown[]).map((option) =>
                typeof option === 'string' || typeof option === 'number'
                  ? { value: String(option), label: String(option) }
                  : option,
              ) as FormFieldOption[];
              return { ...field, options: optionList };
            }
            // Legacy key→label maps (e.g. {yes: 'Yes'}) — only when every value
            // is scalar. Other records are settings bags (unit, max_selections,
            // location-picker config) that must pass through untouched.
            const entries = Object.entries(optionsBag);
            if (entries.length === 0 || entries.some(([, v]) => typeof v !== 'string' && typeof v !== 'number')) {
              return field;
            }
            const optionsArray = entries.map(([key, value]) => ({
              value: key,
              label: typeof value === 'string' ? value : key
            }));
            return { ...field, options: optionsArray };
          }

          return field;
        })
      }));
    }

    return transformedConfig;
  }, [formConfig, dynamicOptions]);

  // Field-level validation errors (pattern, minLength/maxLength, min/max)
  // Declared after transformedFormConfig because it depends on the transformed
  // sections (which include merged dynamic options).
  const fieldValidationErrors = useMemo(() => {
    if (!transformedFormConfig?.form_schema?.sections) return {};
    const visibleSections = transformedFormConfig.form_schema.sections.filter((section) => {
      if (ruleResults?.hiddenSections.has(section.key)) return false;
      if (branchingHiddenSections.has(section.key)) return false;
      return true;
    });
    return collectFieldValidationErrors(visibleSections, formData);
  }, [transformedFormConfig, formData, ruleResults, branchingHiddenSections]);
  const hasFieldValidationErrors = Object.keys(fieldValidationErrors).length > 0;

  // Use customSchema directly if provided (bypasses API fetch).
  // Track the schema ID/reference to avoid re-running when the parent
  // passes a structurally identical but reference-different object. The ID
  // includes a content hash so an edited schema that keeps the same
  // form_key/version (e.g. AI wizard draft edits) still re-applies.
  const customSchemaIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!customSchema) return;
    const schemaId = `${customSchema.form_key ?? ''}_v${customSchema.version ?? 0}_${hashFormSchemaContent(customSchema)}`;
    if (customSchemaIdRef.current === schemaId) return;
    customSchemaIdRef.current = schemaId;

    setFormConfig(customSchema);
    const initialExpanded: Record<string, boolean> = {};
    if (customSchema.form_schema?.sections) {
      customSchema.form_schema.sections.forEach((section: FormSection) => {
        initialExpanded[section.key] = section.default_expanded !== false;
      });
    }
    setExpandedSections(initialExpanded);
    setLoading(false);
  }, [customSchema]);

  // Load form configuration from API (with AbortController for strict-mode cleanup)
  useEffect(() => {
    if (customSchema) return;
    const abortController = new AbortController();

    const loadFormConfig = async () => {
      try {
        setLoading(true);

        const authToken = authService.getTokens()?.access_token || null;
        const response = await fetch(`/api/v1/forms/config/${formKey}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
          },
          signal: abortController.signal,
        });

        if (abortController.signal.aborted) return;

        if (!response.ok) {
          console.warn(`Form configuration not found for ${formKey}, using default config`);
          setLoading(false);
          return;
        }

        const result = await response.json();
        if (abortController.signal.aborted) return;

        if (result.success) {
          setFormConfig(result.data);

          const initialExpanded: Record<string, boolean> = {};
          if (result.data.form_schema?.sections) {
            result.data.form_schema.sections.forEach((section: FormSection) => {
              initialExpanded[section.key] = section.default_expanded !== false;
            });
          }
          setExpandedSections(initialExpanded);

          if (mode === 'create') {
            const defaultValues: FormDataValues = {};
            if (result.data.form_schema?.sections) {
              result.data.form_schema.sections.forEach((section: FormSection) => {
                section.fields.forEach((field: FormField) => {
                  // Builder writes default_value (snake_case); some legacy
                  // schemas may use defaultValue (camelCase). Check both.
                  const defaultValue = field.default_value ?? field.defaultValue;
                  if (defaultValue !== undefined && defaultValue !== null) {
                    if (formData[field.key] === undefined || formData[field.key] === null || formData[field.key] === '') {
                      defaultValues[field.key] = defaultValue as FieldValue;
                    }
                  }
                });
              });
            }

            if (Object.keys(defaultValues).length > 0) {
              onDataChange({ ...formData, ...defaultValues });
            }
          }

          await loadDynamicOptions(abortController.signal);
        } else {
          throw new Error(result.error || 'Failed to load form configuration');
        }
      } catch (err) {
        if ((err as { name?: unknown })?.name === 'AbortError') return;
        console.error('Error loading form config:', err);
        setError(err instanceof Error ? err.message : 'Failed to load form configuration');
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadFormConfig();
    return () => { abortController.abort(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey, customSchema, loadDynamicOptions]);

  // ── Phase 6: URL parameter pre-fill (runs once after form config loads) ──
  const urlPrefilledRef = useRef(false);
  useEffect(() => {
    if (loading || !formConfig || mode === 'view') return;
    if (urlPrefilledRef.current) return;
    if (typeof window === 'undefined') return;
    try {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.size === 0) { urlPrefilledRef.current = true; return; }
      const prefill: Record<string, string> = {};
      urlParams.forEach((val, key) => {
        if (formData[key] === undefined || formData[key] === null || formData[key] === '') {
          prefill[key] = val;
        }
      });
      urlPrefilledRef.current = true;
      if (Object.keys(prefill).length > 0) {
        onDataChange({ ...formData, ...prefill });
      }
    } catch {
      urlPrefilledRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, formConfig]);

  // Check for saved draft after form config loads (only for create/edit modes)
  useEffect(() => {
    if (loading || mode === 'view') return;
    let cancelled = false;
    void loadDraft().then((draft) => {
      if (!cancelled && draft) {
        setDraftTimestamp(draft.savedAt);
        setDraftStatus('available');
      }
    });
    return () => { cancelled = true; };
  }, [loading, mode, loadDraft]);

  // ── Phase 9: Cache form config for offline use ───────────────
  useEffect(() => {
    if (formConfig && formKey) {
      masterSync.cacheOperationalFormConfig(formKey, formConfig).catch(() => { /* best effort */ });
    }
  }, [formConfig, formKey]);

  useEffect(() => {
    void refreshPendingCount();

    const handlePendingChanged = () => {
      void refreshPendingCount();
    };

    window.addEventListener(OPERATIONS_SYNC_PENDING_CHANGED_EVENT, handlePendingChanged);
    return () => {
      window.removeEventListener(OPERATIONS_SYNC_PENDING_CHANGED_EVENT, handlePendingChanged);
    };
  }, [refreshPendingCount]);

  const resolvePositiveInt = useCallback((value: unknown, fallback: number): number => {
    const normalized = Number(value);
    return Number.isFinite(normalized) && normalized > 0 ? Math.floor(normalized) : fallback;
  }, []);

  const getRepeatableSectionInstances = useCallback((section: FormSection): FormDataValues[] => {
    const rawValue = formData[section.key];
    const providedInstances = Array.isArray(rawValue)
      ? rawValue.map((entry) => (
          entry && typeof entry === 'object' && !Array.isArray(entry) ? (entry as FormDataValues) : {}
        ))
      : [];

    const fixedInstances = (
      section.repeat_mode === 'fixed'
      || section.lock_instance_count === true
      || section.fixed_instances != null
    )
      ? resolvePositiveInt(section.fixed_instances ?? section.min_instances ?? section.min_repeats ?? 1, 1)
      : null;

    const minInstances = fixedInstances ?? resolvePositiveInt(section.min_instances ?? section.min_repeats ?? 1, 1);

    if (fixedInstances != null) {
      if (providedInstances.length > fixedInstances) {
        return providedInstances.slice(0, fixedInstances);
      }

      if (providedInstances.length < fixedInstances) {
        return [
          ...providedInstances,
          ...Array.from({ length: fixedInstances - providedInstances.length }, () => ({})),
        ];
      }

      return providedInstances;
    }

    if (providedInstances.length >= minInstances) {
      return providedInstances;
    }

    return [
      ...providedInstances,
      ...Array.from({ length: minInstances - providedInstances.length }, () => ({})),
    ];
  }, [formData, resolvePositiveInt]);

  const hasFieldValue = useCallback((value: unknown): boolean => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return value !== undefined && value !== null && value !== '';
  }, []);

  // Check if a section is completed based on its validation mode
  const checkSectionCompletion = useCallback((section: FormSection): boolean => {
    const { validation_mode, fields } = section;

    if (section.is_repeatable) {
      const instances = getRepeatableSectionInstances(section as FormSection);
      const requiredFields = fields.filter((field) => (
        field.required === true || field.is_required === true
      ));

      if (validation_mode === 'none') {
        return instances.some((instance) => fields.some((field) => hasFieldValue(instance[field.key])));
      }

      if (validation_mode === 'any') {
        if (requiredFields.length > 0) {
          return instances.some((instance) => requiredFields.some((field) => hasFieldValue(instance[field.key])));
        }

        return instances.some((instance) => fields.some((field) => hasFieldValue(instance[field.key])));
      }

      return instances.length > 0 && instances.every((instance) => (
        requiredFields.length > 0
          ? requiredFields.every((field) => hasFieldValue(instance[field.key]))
          : fields.some((field) => hasFieldValue(instance[field.key]))
      ));
    }
    
    // If validation mode is 'none', only show complete if at least one field has a value
    if (validation_mode === 'none') {
      return fields.some((field) => {
        const value = formData[field.key];
        return hasFieldValue(value);
      });
    }

    // Check both 'required' and 'is_required' for compatibility
    const requiredFields = fields.filter((field) => 
      field.required === true || field.is_required === true
    );
    const completedRequiredFields = requiredFields.filter((field) => {
      const value = formData[field.key];
      return hasFieldValue(value);
    });

    if (validation_mode === 'all') {
      // All required fields must be completed
      return completedRequiredFields.length === requiredFields.length && requiredFields.length > 0;
    } else if (validation_mode === 'any') {
      // At least one required field must be completed, or if no required fields, any field
      if (requiredFields.length > 0) {
        return completedRequiredFields.length > 0;
      } else {
        // No required fields, check if any field has a value
        return fields.some((field) => {
          const value = formData[field.key];
          return hasFieldValue(value);
        });
      }
    }

    return false;
  }, [formData, getRepeatableSectionInstances, hasFieldValue]);

  // ── Step view helpers (per-section or per-field) ───────────────────────────
  const steps = useMemo(() => {
    const raw = transformedFormConfig?.form_schema?.sections || [];
    return buildFormSteps(raw, layout, formData, ruleResults ? {
      hiddenFields: ruleResults.hiddenFields,
      shownFields: ruleResults.shownFields,
    } : null);
  }, [transformedFormConfig, layout, formData, ruleResults]);

  useEffect(() => {
    setWizardStep(prev => Math.min(prev, Math.max(0, steps.length - 1)));
  }, [steps.length]);

  const currentStep = steps[wizardStep] || steps[0];
  const currentSectionForStep = currentStep ? getStepSection(currentStep) : null;

  const goNext = useCallback(() => {
    if (!currentSectionForStep) return;
    if (isStepView && layout.block_skip_required && !checkSectionCompletion(currentSectionForStep)) {
      setError('Please complete all required fields before continuing.');
      return;
    }
    setError(null);
    const goto = currentSectionForStep.goto_section;
    const isLastFieldOfSection = steps[wizardStep + 1]?.section.key !== currentSectionForStep.key;
    if (goto && goto !== '__submit') {
      const targetIdx = steps.findIndex((s) => s.section.key === goto);
      if (targetIdx >= 0) { setWizardStep(targetIdx); return; }
    }
    if (goto === '__submit' && isLastFieldOfSection) return;
    if (wizardStep < steps.length - 1) {
      setWizardStep((s: number) => Math.min(s + 1, steps.length - 1));
    }
  }, [currentSectionForStep, checkSectionCompletion, isStepView, layout.block_skip_required, steps, wizardStep]);

  // Auto-advance on single-answer fields when layout.auto_advance_single is on
  const previousFormDataRef = useRef<Record<string, unknown>>({});
  useEffect(() => {
    if (!isStepView || !layout.auto_advance_single || !currentStep || currentStep.type !== 'field') return;
    const field = currentStep.field;
    if (!isAutoAdvanceField(field)) return;

    const isFirstSnapshot = Object.keys(previousFormDataRef.current).length === 0;
    const currentValue = formData[field.key];
    const prevValue = previousFormDataRef.current[field.key];
    const isEmpty = (v: unknown) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

    if (!isFirstSnapshot && !isEmpty(currentValue) && isEmpty(prevValue) && !checkSectionCompletion(currentSectionForStep!)) {
      // do not advance if the field fails required/completion checks
    } else if (!isFirstSnapshot && !isEmpty(currentValue) && isEmpty(prevValue)) {
      goNext();
    }

    previousFormDataRef.current = { ...formData };
  }, [formData, isStepView, layout.auto_advance_single, currentStep, currentSectionForStep, checkSectionCompletion, goNext]);

  // Generate the form title based on mode and entity type
  const getFormTitle = (): string => {
    if (!formConfig) return '';
    
    const entityType = formConfig.entity_type;
    const isCreate = mode === 'create';
    
    // Convert entity type to readable format
    let readableEntity = '';
    if (entityType === 'employees') {
      readableEntity = 'Employee';
    } else if (entityType === 'clients') {
      readableEntity = 'Client';
    } else {
      // Fallback: capitalize first letter and remove 's' if plural
      readableEntity = entityType.charAt(0).toUpperCase() + entityType.slice(1);
      if (readableEntity.endsWith('s')) {
        readableEntity = readableEntity.slice(0, -1);
      }
    }
    
    return `${isCreate ? 'New' : 'Edit'} ${readableEntity}`;
  };

  // Check if a section is empty (no fields have values)
  const checkSectionEmpty = (section: FormSection): boolean => {
    if (section.is_repeatable) {
      const instances = getRepeatableSectionInstances(section as FormSection);
      return !instances.some((instance) => section.fields.some((field) => hasFieldValue(instance[field.key])));
    }

    return !section.fields.some((field) => {
      const value = formData[field.key];
      return hasFieldValue(value);
    });
  };

  const checkSectionVisibility = useCallback((section: FormSection): boolean => {
    // Rules-engine section visibility takes priority
    if (ruleResults?.shownSections.has(section.key)) return true;
    if (ruleResults?.hiddenSections.has(section.key)) return false;
    // Branching (goto_section) hidden sections
    if (branchingHiddenSections.has(section.key)) return false;

    // Legacy conditional_logic fallback
    const logic = section.conditional_logic;

    if (!logic || typeof logic !== 'object') {
      return true;
    }

    const dependsOn = logic.depends_on ?? logic.dependsOn ?? logic.field;
    if (!dependsOn || typeof dependsOn !== 'string') {
      return true;
    }

    const currentValue = formData[dependsOn];
    const expectedValue = logic.show_when ?? logic.equals ?? logic.value;

    if (expectedValue === undefined) {
      return Boolean(currentValue);
    }

    if (Array.isArray(currentValue)) {
      return currentValue.includes(expectedValue);
    }

    return currentValue === expectedValue;
  }, [formData, ruleResults, branchingHiddenSections]);

  // Toggle section expanded state (independent — matches OperationalFormModal;
  // accordion-style closing of other sections is incompatible with the
  // "all expanded by default" behavior)
  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  // ============================================================================
  // AUTO-CALCULATION HELPERS
  // ============================================================================
  
  /**
   * Calculate end datetime from start datetime + duration in hours
   */
  const calculateEndTime = (startDateTime: string, durationHours: number): string => {
    if (!startDateTime || !durationHours) return '';
    const start = new Date(startDateTime);
    if (isNaN(start.getTime())) return '';
    const end = new Date(start.getTime() + durationHours * 60 * 60 * 1000);
    // Format as datetime-local value (YYYY-MM-DDTHH:mm)
    return end.toISOString().slice(0, 16);
  };
  
  /**
   * Calculate duration in hours from start and end datetimes
   */
  const calculateDuration = (startDateTime: string, endDateTime: string): number | null => {
    if (!startDateTime || !endDateTime) return null;
    const start = new Date(startDateTime);
    const end = new Date(endDateTime);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    const diffMs = end.getTime() - start.getTime();
    if (diffMs < 0) return null; // End before start
    // Return hours rounded to 2 decimal places
    return Math.round((diffMs / (60 * 60 * 1000)) * 4) / 4; // Round to nearest 0.25
  };
  
  /**
   * Process auto-calculations based on field metadata
   * Returns additional fields to update
   */
  const processAutoCalculations = (fieldKey: string, value: FieldValue, currentData: FormDataValues): FormDataValues => {
    const additionalUpdates: FormDataValues = {};
    
    // Date/Time auto-calculations for appointment scheduling
    // When scheduled_start changes and we have duration, calculate end
    if (fieldKey === 'scheduled_start' && value) {
      const duration = currentData.estimated_hours;
      if (duration && Number(duration) > 0) {
        const calculatedEnd = calculateEndTime(String(value), Number(duration));
        if (calculatedEnd) {
          additionalUpdates.scheduled_end = calculatedEnd;
        }
      }
    }
    
    // When duration changes and we have start, calculate end
    if (fieldKey === 'estimated_hours' && value) {
      const start = currentData.scheduled_start;
      if (start) {
        const calculatedEnd = calculateEndTime(String(start), Number(value));
        if (calculatedEnd) {
          additionalUpdates.scheduled_end = calculatedEnd;
        }
      }
    }
    
    // When end time changes and we have start, calculate duration
    if (fieldKey === 'scheduled_end' && value) {
      const start = currentData.scheduled_start;
      if (start) {
        const calculatedDuration = calculateDuration(String(start), String(value));
        if (calculatedDuration !== null && calculatedDuration > 0) {
          additionalUpdates.estimated_hours = calculatedDuration;
        }
      }
    }
    
    return additionalUpdates;
  };

  // Handle field value changes with auto-calculation support
  const handleFieldChange = (fieldKey: string, value: FieldValue) => {
    // First apply the direct change
    let newData = { ...formData, [fieldKey]: value };
    
    // Then process any auto-calculations
    const autoCalculatedFields = processAutoCalculations(fieldKey, value, newData);
    if (Object.keys(autoCalculatedFields).length > 0) {
      newData = { ...newData, ...autoCalculatedFields };
    }
    
    onDataChange(newData);
    // Auto-save draft to localStorage
    scheduleDraftSave(newData);
  };

  // Validate all visible required sections
  const validateForm = useCallback(async (): Promise<{ isValid: boolean; errors: string[] }> => {
    const errors: string[] = [];

    if (!formConfig?.form_schema?.sections) {
      return { isValid: true, errors };
    }

    // ── Field-level validation errors (pattern, minLength/maxLength, min/max) ──
    if (hasFieldValidationErrors) {
      for (const [fieldKey, msg] of Object.entries(fieldValidationErrors)) {
        errors.push(msg || `Field "${fieldKey}" has an invalid value`);
      }
    }

    // ── Rules-engine blockers ──
    if (ruleResults?.hasBlockers) {
      if (ruleResults.blockerMessages.length > 0) {
        errors.push(...ruleResults.blockerMessages);
      } else {
        errors.push('Submission is blocked by form rules.');
      }
    }

    const visibleSections = formConfig.form_schema.sections.filter((section) =>
      checkSectionVisibility(section)
    );

    for (const section of visibleSections) {
      const requiredFields = section.fields?.filter((field) =>
        field.required === true || field.is_required === true
      ) || [];

      const validationMode = section.validation_mode || 'all';

      if (section.is_repeatable) {
        const instances = getRepeatableSectionInstances(section as FormSection);

        // Enforce minimum instance count — count only non-empty instances
        // (instances with at least one filled field). Padded empty instances
        // must not count toward the minimum. This is the core fix for the
        // "8-of-10" problem where a form with fixed_instances=10 could pass
        // with only 8 filled instances.
        const fixedInstances = (
          section.repeat_mode === 'fixed'
          || section.lock_instance_count === true
          || section.fixed_instances != null
        )
          ? resolvePositiveInt(section.fixed_instances ?? section.min_instances ?? section.min_repeats ?? 1, 1)
          : null;
        const minInstances = fixedInstances ?? resolvePositiveInt(section.min_instances ?? section.min_repeats ?? 1, 1);

        const nonEmptyCount = instances.filter((instance) =>
          (section.fields || []).some((field) => hasFieldValue(instance[field.key]))
        ).length;

        if (nonEmptyCount < minInstances) {
          errors.push(`"${section.title || section.key}": ${nonEmptyCount} of ${minInstances} required instances completed (${minInstances - nonEmptyCount} remaining)`);
        }

        // Skip per-field validation if there are no required fields
        if (requiredFields.length === 0) continue;

        if (validationMode === 'all') {
          instances.forEach((instance, index) => {
            const missingFields = requiredFields.filter((field) => !hasFieldValue(instance[field.key]));

            if (missingFields.length > 0) {
              const fieldLabels = missingFields.map((field) => field.label || field.key).join(', ');
              errors.push(`"${section.title || section.key}" instance ${index + 1}: Missing required fields - ${fieldLabels}`);
            }
          });
        } else if (validationMode === 'any') {
          instances.forEach((instance, index) => {
            const hasAnyValue = requiredFields.some((field) => hasFieldValue(instance[field.key]));
            if (!hasAnyValue) {
              errors.push(`"${section.title || section.key}" instance ${index + 1}: At least one required field must be filled`);
            }
          });
        }

        continue;
      }

      // Non-repeatable sections: skip if no required fields
      if (requiredFields.length === 0) continue;

      if (validationMode === 'all') {
        const missingFields = requiredFields.filter((field) => {
          const value = formData[field.key];
          return !hasFieldValue(value);
        });

        if (missingFields.length > 0) {
          const fieldLabels = missingFields.map((f) => f.label || f.key).join(', ');
          errors.push(`"${section.title || section.key}": Missing required fields - ${fieldLabels}`);
        }
      } else if (validationMode === 'any') {
        const hasAnyValue = requiredFields.some((field) => {
          const value = formData[field.key];
          return hasFieldValue(value);
        });

        if (!hasAnyValue) {
          errors.push(`"${section.title || section.key}": At least one required field must be filled`);
        }
      }
    }

    if (formData.shift_template_id) {
      const shiftTemplateId = parseInt(String(formData.shift_template_id));

      try {
        const token = authService.getTokens()?.access_token;

        if (token) {
          const shiftResponse = await fetch(`/api/v1/time-attendance/shifts/${shiftTemplateId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (shiftResponse.ok) {
            const shiftData = await shiftResponse.json();
            const shift = shiftData.shift || shiftData;

            if (shift.allow_whatsapp_clockin) {
              if (typeof formData.phone !== 'string' || formData.phone.trim() === '') {
                errors.push(`📱 Phone number is REQUIRED for "${shift.name}" shift (WhatsApp clock-in enabled)`);
              } else {
                const whatsappResponse = await fetch('/api/v1/whatsapp/verify-number', {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({ phone: formData.phone })
                });

                if (whatsappResponse.ok) {
                  const whatsappData = await whatsappResponse.json();

                  if (whatsappData.serviceError) {
                    errors.push(`⚠️ Could not verify WhatsApp for "${shift.name}" shift.\n\nWhatsApp verification service is unavailable. Please try again.`);
                  } else if (!whatsappData.exists || !whatsappData.isWhatsApp) {
                    errors.push(`📱 Phone number ${formData.phone} does NOT have an active WhatsApp account.\n\nWhatsApp is REQUIRED for "${shift.name}" shift.\n\nPlease provide a valid WhatsApp number.`);
                  }
                } else {
                  errors.push(`❌ Could not verify WhatsApp status for ${formData.phone}.\n\nPlease check the number is correct and has WhatsApp installed.`);
                }
              }
            }
          }
        }
      } catch (error) {
        if (formData.shift_template_id) {
          errors.push(`⚠️ Could not validate shift requirements. Please try again or contact support.`);
        }
      }
    }

    return { isValid: errors.length === 0, errors };
  }, [formConfig, checkSectionVisibility, getRepeatableSectionInstances, formData, hasFieldValue, hasFieldValidationErrors, fieldValidationErrors, ruleResults, resolvePositiveInt]);

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent double submission
    if (submitting) {
      return;
    }

    try {
      setSubmitting(true);

      // Validate form before submission
      const validation = await validateForm();
      
      if (!validation.isValid) {
        // Show validation errors
        const errorMessage = `Please complete the following sections:\n\n${validation.errors.join('\n')}`;
        notify.error(errorMessage);
        
        // Scroll to first incomplete section if possible
        const firstIncompleteSection = formConfig?.form_schema?.sections?.find((section) => {
          if (!checkSectionVisibility(section)) return false;
          const requiredFields = section.fields?.filter((f) => f.is_required || f.required) || [];
          if (requiredFields.length === 0) return false;
          return !checkSectionCompletion(section);
        });
        
        if (firstIncompleteSection) {
          // Open the first incomplete section
          setExpandedSections(prev => ({
            ...prev,
            [firstIncompleteSection.key]: true
          }));
        }
        
        return;
      }
      
      // Validation passed, submit the form
      if (onSubmit) {
        await onSubmit(formData);
        // Clear the localStorage draft after successful submission
        await clearDraft();
        // Show the success page if the form has a themed success config
        setSubmitted(true);
      }
    } finally {
      setSubmitting(false);
    }
  }, [submitting, setSubmitting, validateForm, onSubmit, formData, setSubmitted, clearDraft, formConfig, setExpandedSections, checkSectionVisibility, checkSectionCompletion]);

  const goBack = useCallback(() => {
    if (!isStepView) return;
    setWizardStep((s: number) => Math.max(0, s - 1));
  }, [isStepView]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isStepView) return;

    const target = e.target as HTMLElement;
    const tag = target.tagName;
    const inputType = (target as HTMLInputElement).type?.toLowerCase();

    const isInteractive = ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(tag);

    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      if (isInteractive) return;
      e.preventDefault();
      goNext();
    }

    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      if (isInteractive) return;
      e.preventDefault();
      goBack();
    }

    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
      if (tag === 'BUTTON' || tag === 'A' || (tag === 'INPUT' && inputType && ['submit', 'button', 'reset', 'radio', 'checkbox'].includes(inputType))) return;
      if (tag === 'SELECT') return;
      if (tag === 'TEXTAREA' || (tag === 'INPUT' && inputType && !['submit', 'button', 'reset', 'radio', 'checkbox'].includes(inputType))) {
        e.preventDefault();
        const isLast = wizardStep >= steps.length - 1 || (currentSectionForStep?.goto_section === '__submit' && steps[wizardStep + 1]?.section.key !== currentSectionForStep?.key);
        if (isLast) {
          void handleSubmit(e as unknown as React.FormEvent<HTMLFormElement>);
        } else {
          goNext();
        }
      }
    }
  }, [isStepView, goNext, goBack, handleSubmit, wizardStep, steps, currentSectionForStep]);

  if (loading) {
    return (
      <SectionLoader
        size="md"
        message="Loading form configuration..."
        subMessage="Preparing sections, fields, and workflow rules."
        primaryColor={hasFormTheme ? ft.accent_color : currentTheme.colors.primary}
        className="p-8"
      />
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Form Configuration Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!formConfig) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
        <div className="text-sm text-yellow-700">Form configuration not found for key: {formKey}</div>
      </div>
    );
  }

  // ── Cover page: if enabled and not yet dismissed ─────────────
  if (!loading && !error && formConfig && coverPageConfig && showCoverPage) {
    return (
      <div
        className={`dynamic-form-builder ${className}`}
        style={hasFormTheme ? { background: ft.bg_color, fontFamily: ft.font_family } : undefined}
      >
        <FormCoverPage
          config={coverPageConfig}
          formTitle={formConfig.title}
          formTheme={themeInput}
          onStart={() => setShowCoverPage(false)}
        />
      </div>
    );
  }

  // ── Success page: after successful submission ────────────────
  if (submitted && hasFormTheme) {
    return (
      <div
        className={`dynamic-form-builder ${className}`}
        style={{ background: ft.bg_color, fontFamily: ft.font_family }}
      >
        <FormSuccessPage
          config={successPageConfig}
          formTheme={themeInput}
          submittedData={successPageConfig.show_summary ? formData : undefined}
          fieldLabels={fieldLabels}
          onSubmitAnother={successPageConfig.allow_another ? () => {
            setSubmitted(false);
            onDataChange({});
          } : undefined}
        />
      </div>
    );
  }

  return (
    <FormRuntimeKeyProvider formKey={formKey}>
      {injectedCSS && <style dangerouslySetInnerHTML={{ __html: injectedCSS }} />}
      <div
        className={`dynamic-form-builder ${scopeClass} ${className} relative`}
        dir={layout.rtl ? 'rtl' : 'ltr'}
        style={hasFormTheme ? {
        background: ft.bg_color,
        fontFamily: ft.font_family,
        fontSize: textSizeScale,
        borderRadius: ft.border_radius ? `${ft.border_radius}px` : undefined,
        padding: hasFormTheme ? '1rem' : undefined,
      } : undefined}
    >
      {/* Phase 5: Background pattern overlay */}
      {patternCSS && (
        <div
          className="absolute inset-0 pointer-events-none z-0"
          style={{
            ...patternCSS,
            opacity: 0.35,
            borderRadius: ft.border_radius ? `${ft.border_radius}px` : undefined,
          }}
        />
      )}

      {/* Phase 5: Background image overlay */}
      {hasFormTheme && ft.bg_image_url && (
        <div
          className="absolute inset-0 pointer-events-none z-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${ft.bg_image_url})`,
            opacity: ft.bg_image_opacity ?? 0.15,
            borderRadius: ft.border_radius ? `${ft.border_radius}px` : undefined,
          }}
        />
      )}

      <form onSubmit={handleSubmit} onKeyDown={handleKeyDown} className="space-y-4 md:space-y-6 relative z-[1]">
        {/* Form Header with Primary Color Background - Only show if showHeader is true */}
        {showHeader && (
          <div 
            className="rounded-lg shadow-sm border overflow-hidden"
            style={{
              backgroundColor: hasFormTheme ? ft.accent_color : currentTheme.colors.primary,
              backgroundImage: hasFormTheme && ft.header_image_url
                ? `linear-gradient(to bottom, rgba(0,0,0,0.45), rgba(0,0,0,0.65)), url(${ft.header_image_url}), ${ft.header_gradient || `linear-gradient(135deg, ${ft.accent_color}, ${ft.accent_color}dd)`}`
                : hasFormTheme
                  ? (ft.header_gradient || `linear-gradient(135deg, ${ft.accent_color}, ${ft.accent_color}dd)`)
                  : `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.primaryDark})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderColor: hasFormTheme ? `${ft.accent_color}40` : undefined,
              borderRadius: hasFormTheme && ft.border_radius ? `${ft.border_radius}px` : undefined,
            }}
          >
            <div className={`text-center ${forceMobileLayout ? 'px-4 py-3' : 'px-4 py-4 md:px-6 md:py-6'}`}>
              {/* Brand logo (if branding is enabled and a logo URL is configured) */}
              {showBranding && brandLogoUrl && (
                <div className="mx-auto mb-2 flex items-center justify-center">
                  <img
                    src={brandLogoUrl}
                    alt="Company logo"
                    className="max-h-10 max-w-[120px] object-contain sm:max-h-12 sm:max-w-[160px]"
                  />
                </div>
              )}
              <h1 className={`font-bold text-white ${forceMobileLayout ? 'text-lg' : 'text-xl md:text-2xl'}`}>
                {formConfig.title || getFormTitle()}
              </h1>
              {formConfig.description && (
                <p className={`mt-1 text-white/90 ${forceMobileLayout ? 'text-xs line-clamp-2' : 'text-sm md:text-base'}`}>{formConfig.description}</p>
              )}
            </div>
          </div>
        )}

        {/* Draft restore banner */}
        {draftStatus === 'available' && mode !== 'view' && (
          <div className={`rounded-lg border px-4 py-3 flex items-center justify-between gap-3 ${
            isDark ? 'bg-amber-900/30 border-amber-700 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}>
            <div className="flex items-center gap-2 min-w-0">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm truncate">
                You have an unsaved draft{draftTimestamp ? ` from ${new Date(draftTimestamp).toLocaleString()}` : ''}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => {
                  void loadDraft().then((draft) => {
                    if (draft) {
                      onDataChange({ ...formData, ...draft.data });
                      setDraftTimestamp(draft.savedAt);
                      setDraftStatus('restored');
                    }
                  });
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  isDark
                    ? 'bg-amber-700 hover:bg-amber-600 text-white'
                    : 'bg-amber-600 hover:bg-amber-700 text-white'
                }`}
              >
                Restore
              </button>
              <button
                type="button"
                onClick={() => { void clearDraft(); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  isDark
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    : 'bg-white hover:bg-gray-100 text-gray-600 border border-gray-300'
                }`}
              >
                Discard
              </button>
            </div>
          </div>
        )}

        {/* Draft auto-save indicator */}
        {draftStatus === 'restored' && mode !== 'view' && (
          <div className={`rounded-lg border px-4 py-2 flex items-center gap-2 text-xs ${
            isDark ? 'bg-green-900/20 border-green-800 text-green-300' : 'bg-green-50 border-green-200 text-green-700'
          }`}>
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Draft restored &amp; auto-saving changes</span>
            <button
              type="button"
              onClick={() => { void clearDraft(); }}
              className={`ml-auto text-xs underline opacity-70 hover:opacity-100`}
            >
              Clear draft
            </button>
          </div>
        )}

        {draftStatus === 'saving' && mode !== 'view' && (
          <div className={`rounded-lg border px-4 py-2 flex items-center gap-2 text-xs ${
            isDark ? 'bg-blue-900/20 border-blue-800 text-blue-300' : 'bg-blue-50 border-blue-200 text-blue-700'
          }`}>
            <svg className="w-4 h-4 flex-shrink-0 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m-.582 0a8.001 8.001 0 0115.356-2M20 20v-5h-.581m.581 0a8.003 8.003 0 01-15.357 2" />
            </svg>
            <span>Saving draft...</span>
          </div>
        )}

        {/* Phase 9: Offline indicator */}
        {!isOnline && (
          <div className="rounded-lg border px-4 py-2 flex items-center gap-2 text-xs bg-orange-50 border-orange-200 text-orange-700 dark:bg-orange-900/20 dark:border-orange-700 dark:text-orange-300">
            <WifiOff className="w-4 h-4 flex-shrink-0" />
            <span>You&apos;re offline — submissions will be queued and synced when you reconnect.</span>
            {pendingCount > 0 && (
              <span className="ml-auto font-medium">{pendingCount} pending</span>
            )}
          </div>
        )}

        {/* Form Sections */}
        {(() => {
          const sortedSections = [...(transformedFormConfig?.form_schema?.sections || [])]
            .sort((a, b) => a.sort_order - b.sort_order);
          const isBranchingWizard = sortedSections.some((s) => s.goto_section);
          const isWizard = isBranchingWizard || isStepView;

          if (isWizard && steps.length > 0) {
            // ── Wizard / stepper mode (per-section or per-field) ─
            const currentSection = currentSectionForStep || sortedSections[0];
            const totalSteps = steps.length;
            const isLastFieldOfSection = steps[wizardStep + 1]?.section.key !== currentSection.key;
            const isLast = wizardStep >= totalSteps - 1
              || (currentSection.goto_section === '__submit' && isLastFieldOfSection);
            const progress = ((wizardStep + 1) / totalSteps) * 100;
            const effectiveSubmitText = formConfig?.form_schema?.submit_button_text || submitButtonText;

            return (
              <>
                {/* Progress indicator */}
                {formConfig?.show_progress_bar !== false && layout.progress_style !== 'none' && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs opacity-60">
                      <span>Step {wizardStep + 1} of {totalSteps}</span>
                      {layout.progress_style === 'bar' && <span>{Math.round(progress)}%</span>}
                    </div>
                    {layout.progress_style === 'bar' ? (
                      <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                        <div
                          className="h-full rounded-full transition-all duration-300"
                          style={{
                            width: `${progress}%`,
                            background: hasFormTheme ? (ft.header_gradient || ft.accent_color) : currentTheme.colors.primary,
                          }}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        {steps.map((_, i) => (
                          <div
                            key={i}
                            className={`h-2 rounded-full transition-all duration-300 ${
                              i === wizardStep ? 'w-6' : 'w-2'
                            } ${
                              i <= wizardStep
                                ? ''
                                : isDark ? 'bg-gray-700' : 'bg-gray-200'
                            }`}
                            style={{
                              background: i <= wizardStep
                                ? (hasFormTheme ? (ft.header_gradient || ft.accent_color) : currentTheme.colors.primary)
                                : undefined,
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Single section — Phase 9: attach swipe ref for mobile navigation */}
                <div ref={swipeContainerRef} className="space-y-4 md:space-y-6">
                  <StepTransitionContainer transition={layout.transition} stepKey={wizardStep}>
                    <DynamicSection
                      key={`${formKey}-${currentSection.key}-${wizardStep}`}
                      section={currentSection}
                      formData={formData}
                      expandedSections={{ [currentSection.key]: true }}
                      onToggle={() => {}}
                      onFieldChange={handleFieldChange}
                      isCompleted={checkSectionCompletion(currentSection)}
                      isEmpty={checkSectionEmpty(currentSection)}
                      enableImageUpload={enableImageUpload}
                      fullFormData={formData}
                      formTheme={hasFormTheme ? ft : undefined}
                      forceMobileLayout={forceMobileLayout}
                      hiddenFields={ruleResults?.hiddenFields}
                      shownFields={ruleResults?.shownFields}
                      dynamicRequired={ruleResults?.dynamicRequired}
                      dynamicUnrequired={ruleResults?.dynamicUnrequired}
                      fieldNumber={layout.show_field_numbers && layout.step_granularity === 'field' ? wizardStep + 1 : undefined}
                      autoFocus
                    />
                  </StepTransitionContainer>
                </div>

                {/* Phase 9: Swipe hint (mobile-only) */}
                <p className="text-center text-[10px] text-gray-400 md:hidden select-none">
                  Swipe left/right to navigate sections
                </p>

                {/* Wizard nav */}
                {mode !== 'view' && (
                  <div className={`rounded-lg shadow-sm border p-4 ${
                    isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                  }`}>
                    <div className={`flex ${stepButtonContainerClass(layout.submit_button_align)}`}>
                      {layout.show_back_button && (
                        <button
                          type="button"
                          onClick={() => setWizardStep((s: number) => Math.max(s - 1, 0))}
                          disabled={wizardStep === 0}
                          className={`btn-theme-secondary disabled:opacity-30 ${stepButtonWidthClass(layout.submit_button_align)}`}
                        >
                          <ChevronLeft className="w-4 h-4 mr-1 inline" />
                          Previous
                        </button>
                      )}

                      {isLast ? (
                        <button
                          type="submit"
                          disabled={submitting}
                          className={`btn-theme-primary disabled:opacity-50 disabled:cursor-not-allowed ${stepButtonWidthClass(layout.submit_button_align)}`}
                        >
                          {submitting ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 inline animate-spin" />
                              Validating...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2 inline" />
                              {effectiveSubmitText}
                            </>
                          )}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={goNext}
                          className={`btn-theme-primary ${stepButtonWidthClass(layout.submit_button_align)}`}
                        >
                          Next
                          <ChevronRight className="w-4 h-4 ml-1 inline" />
                        </button>
                      )}
                    </div>
                    <p className={`mt-2 text-center text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'} select-none`}>
                      {currentStep?.type === 'field' && (currentStep.field.type === 'textarea' || currentStep.field.type === 'rich_text')
                        ? <>Press <kbd className={`rounded px-1 py-0.5 text-[10px] font-mono ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>SHIFT + ENTER</kbd> for a new line, <kbd className={`rounded px-1 py-0.5 text-[10px] font-mono ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>ENTER</kbd> to continue</>
                        : <>Press <kbd className={`rounded px-1 py-0.5 text-[10px] font-mono ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>ENTER</kbd> to continue</>}
                    </p>
                  </div>
                )}
              </>
            );
          }

          // ── Default: all sections expanded ───────────────────
          // Skip rules/branching-hidden sections like OperationalFormModal does.
          const visibleSections = sortedSections.filter((section) => checkSectionVisibility(section));
          const showFieldNumbers = layout.show_field_numbers === true;
          const fieldNumberStartMap: Record<string, number> = {};
          if (showFieldNumbers) {
            let offset = 1;
            visibleSections.forEach((section) => {
              fieldNumberStartMap[section.key] = offset;
              const instanceCount = section.is_repeatable
                ? getRepeatableSectionInstances(section).length
                : 1;
              offset += (section.fields?.length || 0) * instanceCount;
            });
          }

          return (
            <>
              <div className="space-y-4 md:space-y-6">
                {visibleSections.map((section, index: number) => (
                  <DynamicSection
                    key={`${formKey}-${section.key}-${index}`}
                    section={section}
                    formData={formData}
                    expandedSections={expandedSections}
                    onToggle={toggleSection}
                    onFieldChange={handleFieldChange}
                    isCompleted={checkSectionCompletion(section)}
                    isEmpty={checkSectionEmpty(section)}
                    enableImageUpload={enableImageUpload}
                    fullFormData={formData}
                    formTheme={hasFormTheme ? ft : undefined}
                    forceMobileLayout={forceMobileLayout}
                    hiddenFields={ruleResults?.hiddenFields}
                    shownFields={ruleResults?.shownFields}
                    dynamicRequired={ruleResults?.dynamicRequired}
                    dynamicUnrequired={ruleResults?.dynamicUnrequired}
                    fieldNumber={fieldNumberStartMap[section.key]}
                  />
                ))}
              </div>

              {/* Computed Fields Summary */}
              {computedFieldResults.length > 0 && computedFieldResults.some(r => {
                const def = formConfig?.form_schema?.computed_fields?.find(d => d.key === r.key);
                return def?.visible !== false && def?.display_in !== 'hidden';
              }) && (
                <div className={`rounded-lg shadow-sm border p-4 md:p-6 ${
                  isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                }`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Calculator className="w-4 h-4 opacity-60" />
                    <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Calculated Values
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {computedFieldResults
                      .filter(r => {
                        const def = formConfig?.form_schema?.computed_fields?.find(d => d.key === r.key);
                        return def?.visible !== false && def?.display_in !== 'hidden';
                      })
                      .map(r => {
                        const isPassFail = r.type === 'pass_fail_result';
                        const isPassing = isPassFail && typeof r.value === 'string' && r.value.toLowerCase() === 'pass';
                        const isFailing = isPassFail && typeof r.value === 'string' && r.value.toLowerCase() === 'fail';
                        return (
                          <div
                            key={r.key}
                            className={`rounded-md p-3 ${
                              isPassFail
                                ? isPassing
                                  ? 'bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800'
                                  : isFailing
                                    ? 'bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800'
                                    : isDark ? 'bg-gray-700/50' : 'bg-gray-50'
                                : isDark ? 'bg-gray-700/50' : 'bg-gray-50'
                            }`}
                          >
                            <div className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {r.label}
                            </div>
                            <div className={`text-lg font-semibold ${
                              isPassFail
                                ? isPassing
                                  ? 'text-green-600 dark:text-green-400'
                                  : isFailing
                                    ? 'text-red-600 dark:text-red-400'
                                    : isDark ? 'text-white' : 'text-gray-900'
                                : isDark ? 'text-white' : 'text-gray-900'
                            }`}>
                              {r.display}
                              {r.unit && <span className="text-sm font-normal ml-1 opacity-60">{r.unit}</span>}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Form Actions (non-wizard) */}
              {onSubmit && showActions && mode !== 'view' && (
                <div className={`rounded-lg shadow-sm border p-4 md:p-6 ${
                  isDark 
                    ? 'bg-gray-800 border-gray-600' 
                    : 'bg-white border-gray-200'
                }`}>
                  <div className={`${listSubmitContainerClass(layout.submit_button_align)} ${layout.submit_button_align === 'stretch' ? 'w-full' : 'flex-col sm:flex-row'} space-y-3 sm:space-y-0 sm:space-x-4`}>
                    <button
                      type="button"
                      onClick={onCancel}
                      className={`btn-theme-secondary order-2 sm:order-1 ${listSubmitButtonClass(layout.submit_button_align)}`}
                    >
                      <X className="w-4 h-4 mr-2 inline" />
                      {cancelButtonText}
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className={`btn-theme-primary order-1 sm:order-2 disabled:opacity-50 disabled:cursor-not-allowed ${listSubmitButtonClass(layout.submit_button_align)}`}
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 inline animate-spin" />
                          Validating...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2 inline" />
                          {formConfig?.form_schema?.submit_button_text || submitButtonText}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* Close button for view mode */}
        {mode === 'view' && showActions && (
          <div className={`rounded-lg shadow-sm border p-4 md:p-6 ${
            isDark 
              ? 'bg-gray-800 border-gray-600' 
              : 'bg-white border-gray-200'
          }`}>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onCancel}
                className="btn-theme-secondary"
              >
                <X className="w-4 h-4 mr-2 inline" />
                Close
              </button>
            </div>
          </div>
        )}

        {/* ── Branding watermark / footer ─────────────────── */}
        {showBranding && (brandLogoUrl || watermarkText) && (
          <div className={`flex items-center justify-center gap-2 py-1.5 text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {brandLogoUrl && (
              <img src={brandLogoUrl} alt="" className="h-3.5 w-auto object-contain opacity-60" />
            )}
            {watermarkText && (
              <span className="font-medium opacity-70">{watermarkText}</span>
            )}
          </div>
        )}
      </form>
    </div>
    </FormRuntimeKeyProvider>
  );
};